import { useContext } from 'react';

import { snakeCaseToCamel } from 'app/shared/utils/string';
import { FlagConfig } from 'app/shared/typings/featureFlags';
import {
  FeatureFlagEnabled,
  GetFeatureFlags,
} from 'app/shared/graphql/featureFlags/queryHooks';
import { FeatureFlagsContext } from 'app/shared/context/FeatureFlags';

import useForceVariants from './useForceVariants';

const isFeatureFlagEnabled = (
  featureFlagName: string,
  featureFlagEnabled: boolean,
  featureFlagForceVariants?: Record<string, string>
) => {
  const forcedVariant = featureFlagForceVariants?.[featureFlagName];

  if (forcedVariant !== undefined) {
    return forcedVariant !== 'a';
  }

  return featureFlagEnabled;
};

const getFeatureFlagsJustAskedFor = (flags?: string[]) =>
  flags?.map((flag: string) => snakeCaseToCamel(flag)) || [];

type FeatureFlagsEnabled = {
  [name: string]: boolean;
};

type ExperimentVariants = {
  [name: string]: string;
};

export const getExperimentVariants = (
  featureFlagsJustLoaded?: FeatureFlagEnabled[],
  experimentVariantsAlreadyAssigned?: ExperimentVariants
): ExperimentVariants => {
  const variants: ExperimentVariants = { ...experimentVariantsAlreadyAssigned };
  featureFlagsJustLoaded?.forEach((flag) => {
    if (flag.name && flag.variant) {
      variants[flag.name] = flag.variant;
    }
  });
  return variants;
};

// Returns object of feature flags in camelCase and boolean values
export const getFeatureFlagsEnabled = (
  featureFlagsJustLoaded?: FeatureFlagEnabled[],
  featureFlagForceVariants?: any,
  featureFlagsAlreadyEnabled?: FeatureFlagsEnabled,
  flags?: string[]
) => {
  const featureFlagsEnabled: FeatureFlagsEnabled = {};

  if (featureFlagsJustLoaded) {
    featureFlagsJustLoaded.forEach((featureFlagResult) => {
      if (featureFlagResult.name) {
        featureFlagsEnabled[featureFlagResult.name] = isFeatureFlagEnabled(
          featureFlagResult.name,
          featureFlagResult.featureFlagEnabled,
          featureFlagForceVariants
        );
      }
    });
  }

  if (featureFlagsAlreadyEnabled) {
    Object.keys(featureFlagsAlreadyEnabled).forEach((featureFlag: string) => {
      if (!flags || getFeatureFlagsJustAskedFor(flags).includes(featureFlag)) {
        featureFlagsEnabled[featureFlag] =
          featureFlagsAlreadyEnabled[featureFlag];
      }
    });
  }

  return featureFlagsEnabled;
};

// Returns feature flags in camelCase
const useFeatureFlagsAlreadyLoadedInfo = () => {
  const featureFlagsContext = useContext(FeatureFlagsContext);
  return {
    featureFlagsAlreadyLoaded: featureFlagsContext?.featureFlagsLoaded || [],
    featureFlagsAlreadyEnabled: featureFlagsContext?.featureFlagsEnabled || {},
    experimentVariantsAlreadyAssigned: {},
  };
};

// Returns feature flags in snake_case
const getFeatureFlagsToLoad = (
  flags?: string[],
  featureFlagsAlreadyLoaded?: string[]
) => {
  if (!flags || flags.length == 0) {
    return [];
  }

  const flagsNotLoaded = featureFlagsAlreadyLoaded
    ? flags.filter(
        (flag: string) =>
          !featureFlagsAlreadyLoaded?.includes(snakeCaseToCamel(flag))
      )
    : flags;

  return flagsNotLoaded;
};

// Typically called like this, e.g.:
//
//  const {
//    featureFlagsEnabled: { newVenueApplications: displayNewVenueApplications },
//  } = useFeatureFlags(['new_venue_applications']);
//
// Then the result is tested like this, e.g.:
//
// if (displayNewVenueApplications) {
//
export const useFeatureFlags = (flags: string[]) => {
  const featureFlagForceVariants = useForceVariants(flags);

  const { featureFlagsAlreadyLoaded, featureFlagsAlreadyEnabled } =
    useFeatureFlagsAlreadyLoadedInfo();

  const featureFlagsToLoad = getFeatureFlagsToLoad(
    flags,
    featureFlagsAlreadyLoaded
  );

  const { data, error, loading, called } = GetFeatureFlags({
    flags: featureFlagsToLoad,
    shouldLoadFlags: featureFlagsToLoad.length > 0,
  });

  const featureFlagsEnabled = getFeatureFlagsEnabled(
    data?.featureFlags,
    featureFlagForceVariants,
    featureFlagsAlreadyEnabled,
    flags
  );

  return {
    featureFlagsEnabled,
    error,
    loading,
    called,
    queryRequired: !!(featureFlagsToLoad.length > 0),
  };
};

export const useFeatureFlagsForFlipperId = (
  flags: string[],
  flipperId: string | undefined,
  flagConfigs?: FlagConfig[],
  loadWithoutFlipperId = false
) => {
  const featureFlagForceVariants = useForceVariants(flags);

  const {
    featureFlagsAlreadyLoaded,
    featureFlagsAlreadyEnabled,
    experimentVariantsAlreadyAssigned,
  } = useFeatureFlagsAlreadyLoadedInfo();

  const featureFlagsToLoad = getFeatureFlagsToLoad(
    flags,
    featureFlagsAlreadyLoaded
  );
  const anyFlagsToLoad = featureFlagsToLoad.length > 0;

  const flagConfigsToLoad = flagConfigs?.filter((fc) =>
    featureFlagsToLoad.includes(fc.name)
  );

  const { data, error, loading, called } = GetFeatureFlags({
    flags: featureFlagsToLoad,
    flagConfigs: flagConfigsToLoad?.length ? flagConfigsToLoad : undefined,
    flipperId,
    shouldLoadFlags: anyFlagsToLoad && (!!flipperId || loadWithoutFlipperId),
  });

  const featureFlagsEnabled = getFeatureFlagsEnabled(
    data?.featureFlags,
    featureFlagForceVariants,
    featureFlagsAlreadyEnabled,
    flags
  );

  const experimentVariants = getExperimentVariants(
    data?.featureFlags,
    experimentVariantsAlreadyAssigned
  );

  return {
    featureFlagsEnabled,
    experimentVariants,
    featureFlagForceVariants,
    error,
    loading,
    called,
  };
};
